import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../style/attendanceContent.css";
import { PageHeader, Card, EmptyState } from "../../components/admin/AdminUI";
import DateInput from "../../components/DateInput";
import { formatDmy } from "../../utils/dateFormat";

const CLASS_OPTIONS = ["OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS", "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE"];

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: "#0d9488" });
const swalSuccess = (text) =>
  Swal.fire({ text, icon: "success", background: "white", color: "black", confirmButtonColor: "#0d9488" });

// `assistant` = halaman dibuka dari portal class assistant: fiturnya sama
// persis, yang berbeda hanya session mana yang dicek dan ke mana dialihkan
// kalau session-nya habis.
const ClassSessionsPage = ({ setLocationAdmin, assistant = false }) => {
  const navigate = useNavigate();
  const address = useLocation();
  const [errorMe, setErrorMe] = useState(false);

  const [classCode, setClassCode] = useState("OT");
  const [batch, setBatch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [sessions, setSessions] = useState([]);
  const [originalTimes, setOriginalTimes] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [batches, setBatches] = useState([]);
  // "CODE|BATCH" jadwal yang sedang dihapus — tombolnya dinonaktifkan selama
  // itu supaya klik ganda tidak mengirim dua request.
  const [deleting, setDeleting] = useState(null);

  const getMe = async () => {
    try {
      await axios.get(assistant ? "/assistant/me" : "/meAdmin");
    } catch (error) {
      setErrorMe(true);
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await axios.get("/classsessions/batches");
      setBatches(res.data || []);
    } catch (error) {
      setBatches([]);
    }
  };

  const fetchSessions = async () => {
    if (!classCode || !batch) return;
    setLoading(true);
    try {
      const res = await axios.get("/classsessions", { params: { classCode, batch } });
      setSessions(res.data);
      const times = {};
      res.data.forEach((s) => {
        times[s.id] = { startTime: s.startTime || "", endTime: s.endTime || "" };
      });
      setOriginalTimes(times);
    } catch (error) {
      // no sessions yet is fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
    fetchBatches();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate(assistant ? "/assistant/login" : "/loginAdmin");
    }
  }, [errorMe, navigate, assistant]);

  useEffect(() => {
    fetchSessions();
  }, [classCode, batch]);

  const handleGenerate = async () => {
    if (!batch || !startDate) {
      return swalError("Batch and start date are required.");
    }
    try {
      await axios.post("/classsessions/generate", { classCode, batch, startDate });
      swalSuccess("10 sessions generated.");
      fetchSessions();
      fetchBatches();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to generate sessions.");
    }
  };

  // Dipakai tombol Delete di kolom kanan tabel Generated Schedules MAUPUN
  // tombol hapus jadwal yang sedang dibuka. `row` (dari daftar batches) dipakai
  // untuk menyebutkan ANGKA PERSIS yang akan hilang di dialog konfirmasi —
  // peringatan umum "data akan hilang" tidak cukup untuk aksi yang tidak bisa
  // dibatalkan.
  const handleDelete = async (targetClassCode, targetBatch, row) => {
    const code = targetClassCode ?? classCode;
    const bt = String(targetBatch ?? batch);
    const info = row || batches.find((b) => b.classCode === code && String(b.batch) === bt);
    const sessionCount = info ? info.sessionCount : 10;
    const attendanceCount = info ? info.attendanceCount : null;

    const lines = [`${sessionCount} sesi ${code} batch ${bt} akan dihapus.`];
    if (attendanceCount > 0) {
      lines.push(`${attendanceCount} baris absensi ikut terhapus permanen — termasuk koreksi manual admin.`);
    } else if (attendanceCount === 0) {
      lines.push("Belum ada absensi tercatat, jadi tidak ada data kehadiran yang hilang.");
    }
    lines.push("Nilai tugas TIDAK terhapus (tersimpan per kelas/batch, bukan per sesi).");

    const result = await Swal.fire({
      title: "Hapus jadwal ini?",
      html: lines.map((l) => `<p style="margin:0 0 8px;font-size:14px">${l}</p>`).join(""),
      icon: "warning",
      background: "white",
      color: "black",
      showCancelButton: true,
      cancelButtonText: "Batal",
      confirmButtonColor: "#d33",
      confirmButtonText: attendanceCount > 0 ? `Ya, hapus ${attendanceCount} absensi juga` : "Ya, hapus",
    });
    if (!result.isConfirmed) return;

    setDeleting(`${code}|${bt}`);
    try {
      const res = await axios.delete("/classsessions", { params: { classCode: code, batch: bt } });
      swalSuccess(res.data?.msg || "Jadwal dihapus.");
      // Kalau yang dihapus adalah jadwal yang sedang dibuka, tabel sesinya
      // ikut dikosongkan supaya layar tidak menampilkan baris yang sudah tidak
      // ada di database.
      if (code === classCode && bt === String(batch)) setSessions([]);
      fetchBatches();
    } catch (error) {
      swalError(error.response?.data?.msg || "Gagal menghapus jadwal.");
    } finally {
      setDeleting(null);
    }
  };

  const handleTimeChange = (id, field, value) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleSaveTime = async (session) => {
    setSavingId(session.id);
    try {
      await axios.patch(`/classsessions/${session.id}`, {
        startTime: session.startTime || null,
        endTime: session.endTime || null,
      });
      setOriginalTimes((prev) => ({
        ...prev,
        [session.id]: { startTime: session.startTime || "", endTime: session.endTime || "" },
      }));
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to save session time.");
    } finally {
      setSavingId(null);
    }
  };

  const isDirty = (session) => {
    const original = originalTimes[session.id];
    if (!original) return false;
    return (session.startTime || "") !== original.startTime || (session.endTime || "") !== original.endTime;
  };

  const byDay = sessions.reduce((acc, s) => {
    (acc[s.sessionDate] = acc[s.sessionDate] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="adm-main">
      <PageHeader
        title="Class Sessions"
        subtitle="Generate the 10 sessions (5 days × 2 sessions) for a class/batch, then fill in the actual start/end time of each session after class runs."
      />

      <Card title="Generate or open a schedule">
        <div className="adm-form-grid adm-form-grid--4" style={{ alignItems: "end" }}>
          <div>
            <label className="form-label">Class</label>
            <select className="form-select" value={classCode} onChange={(e) => setClassCode(e.target.value)}>
              {CLASS_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Batch</label>
            <input className="form-control" value={batch} onChange={(e) => setBatch(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 4" />
          </div>
          <div>
            <label className="form-label">Start Date (day 1)</label>
            <DateInput className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="adm-btn adm-btn--primary" onClick={handleGenerate}>
              <i className="bi bi-magic"></i>
              Generate
            </button>
            {sessions.length > 0 && (
              <button type="button" className="adm-btn adm-btn--danger" onClick={() => handleDelete()}>
                Delete
              </button>
            )}
          </div>
        </div>
      </Card>

      {batches.length > 0 && (
        <Card title="Generated Schedules" subtitle="Pick a schedule to view and fill in its session times." flush className="adm-section">
          <div className="adm-table-wrap">
            <table className="adm-table" style={{ minWidth: "640px" }}>
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Batch</th>
                  <th>Dates</th>
                  <th>Times filled</th>
                  <th className="adm-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  // Baris yang sedang dibuka ditandai supaya admin tahu jadwal
                  // di bawah ini milik batch yang mana.
                  const isActive = b.classCode === classCode && String(b.batch) === String(batch);
                  const complete = b.filledCount === b.sessionCount;
                  return (
                    <tr key={`${b.classCode}-${b.batch}`} className={isActive ? "adm-row-active" : ""}>
                      <td>
                        <div className="adm-cell-title">{b.classCode}</div>
                        <div className="adm-cell-meta">{b.className}</div>
                      </td>
                      <td>{b.batch}</td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--adm-text-2)" }}>
                        {formatDmy(b.firstDate)} → {formatDmy(b.lastDate)}
                      </td>
                      <td>
                        <span className={`adm-status ${complete ? "adm-status--success" : "adm-status--warning"}`}>
                          {b.filledCount}/{b.sessionCount}
                        </span>
                      </td>
                      <td className="adm-col-actions">
                        <div className="adm-actions">
                          <button
                            type="button"
                            className="adm-btn adm-btn--secondary adm-btn--sm"
                            onClick={() => {
                              setClassCode(b.classCode);
                              setBatch(String(b.batch));
                            }}
                          >
                            {isActive ? "Viewing" : "View Schedule"}
                          </button>
                          <button
                            type="button"
                            className="adm-icon-btn adm-icon-btn--danger"
                            disabled={deleting === `${b.classCode}|${b.batch}`}
                            aria-label="Delete schedule"
                            title={
                              b.attendanceCount > 0
                                ? `Hapus jadwal ini beserta ${b.attendanceCount} baris absensinya`
                                : "Hapus jadwal ini (belum ada absensi)"
                            }
                            onClick={() => handleDelete(b.classCode, String(b.batch), b)}
                          >
                            {deleting === `${b.classCode}|${b.batch}` ? "…" : <i className="bi bi-trash"></i>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="adm-section">
        {loading ? (
          <div className="adm-card"><EmptyState icon="bi-hourglass" title="Loading sessions…" /></div>
        ) : sessions.length === 0 ? (
          // Tanpa batch, fetchSessions() memang tidak pernah jalan — mengatakan
          // "belum ada sesi" di situ menyesatkan, apalagi kalau daftar jadwal di
          // atasnya justru berisi.
          <div className="adm-card">
            <EmptyState
              icon="bi-calendar3"
              title={batch ? "No sessions yet" : "No schedule selected"}
              text={
                batch
                  ? `No sessions generated yet for ${classCode} batch ${batch}.`
                  : "Enter a batch above, or pick one from the list to view its schedule."
              }
            />
          </div>
        ) : (
          <>
            <p className="adm-form-section-title">
              {classCode} · Batch {batch}
            </p>
            <div className="adm-day-grid">
              {Object.entries(byDay).map(([date, daySessions]) => (
                <Card key={date} title={`Day ${daySessions[0].dayNumber}`} subtitle={formatDmy(date)}>
                  {daySessions
                    .sort((a, b) => a.sessionNumber - b.sessionNumber)
                    .map((s) => (
                      <div key={s.id} className="adm-session-row">
                        <span className="adm-session-label">Session {s.sessionNumber}</span>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          aria-label={`Session ${s.sessionNumber} start`}
                          value={s.startTime || ""}
                          onChange={(e) => handleTimeChange(s.id, "startTime", e.target.value)}
                        />
                        <span className="adm-muted-text">–</span>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          aria-label={`Session ${s.sessionNumber} end`}
                          value={s.endTime || ""}
                          onChange={(e) => handleTimeChange(s.id, "endTime", e.target.value)}
                        />
                        <button
                          type="button"
                          className={`adm-btn adm-btn--sm ${isDirty(s) ? "adm-btn--primary" : "adm-btn--secondary"}`}
                          disabled={savingId === s.id}
                          onClick={() => handleSaveTime(s)}
                        >
                          {savingId === s.id ? "…" : "Save"}
                        </button>
                        <span className={`adm-session-state ${isDirty(s) ? "is-dirty" : "is-saved"}`}>
                          {isDirty(s) ? "Not saved" : "Saved"}
                        </span>
                      </div>
                    ))}
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClassSessionsPage;
